const toNum = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const keyOf = (obj: any, idKey = "Id", nameKey = "Name") =>
  obj?.[idKey] != null && obj?.[idKey] !== ""
    ? String(obj[idKey])
    : String(obj?.[nameKey] ?? "");

export function useProductSoldSummary() {
  const summarizeAmountByTypeFromSubItems = (
    data: Record<string, any>,
    opts: {
      dropZeroSubItems?: boolean;
      dropZeroCatalogs?: boolean;
    } = {}
  ): summary_by_type => {
    const {
      dropZeroSubItems = true,
      dropZeroCatalogs = true,
    } = opts;

    // 1) flatten catalogs
    const catalogs = Object.entries(data ?? {}).flatMap(([typeKey, groupsObj]) =>
      Object.values(groupsObj ?? {})
        .filter(Array.isArray)
        .flatMap((arr: any[]) => arr)
        .map((catalog: any) => ({ typeKey, catalog }))
    );


     
    // 2) aggregate: type -> catalog -> subitems
    const agg = catalogs.reduce((acc: any, { typeKey, catalog }) => {
      const typeBucket = (acc[typeKey] ??= {
        totalamount: 0,
        catalogMap: {},
      });

      const cKey = keyOf(catalog, "Id", "Name");
      const cSlot =
        (typeBucket.catalogMap[cKey] ??= {
          id: catalog?.Id,
          name: catalog?.Name,
          type: catalog?.Type ?? typeKey,
          row: catalog?.Row,
          subMap: {} as Record<string, sub_item>,
        });

      const items = Array.isArray(catalog?.Items) ? catalog.Items : [];

      items.forEach((it: any) => {
        const subs = Array.isArray(it?.SubItems) ? it.SubItems : [];
        if (subs.length === 0) return;

        const itemName = String(it?.Name ?? "");
        const itemKey = keyOf(it, "Id", "Name");

        subs.forEach((s: any) => {
          const sKey = keyOf(s, "Id", "Name");
          const subName = String(s?.Name ?? "").trim();

          const slot =
            (cSlot.subMap[sKey] ??= {
              id: s?.Id,
              name: subName || itemName,
              itemName,
              subName,
              itemKey,
              quantity: 0,
              amount: 0,
            });

          slot.quantity += toNum(s?.quantity);
          slot.amount += toNum(s?.amount);
        });
      });

      return acc;
    }, {});

    // 3) build output
    const out: summary_by_type = Object.entries(agg).reduce(
      (res: any, [typeKey, bucket]: any) => {
        const catalogsArr: catalog_summary[] = Object.values(bucket.catalogMap).map((c: any) => {
          // Preserve catalog menu order (insertion order from catalog.Items → SubItems);
          // do NOT sort by amount — the printed report must follow the original menu layout.
          const subItems = Object.values(c.subMap)
            .filter((r: any) => (dropZeroSubItems ? toNum(r.quantity) > 0 : true));

          const totalamount = subItems.reduce(
            (sum: number, r: any) => sum + toNum(r.amount),
            0
          );

          return {
            id: c.id,
            name: c.name,
            type: c.type,
            row: c.row,
            totalamount,
            subItems,
          };
        });

        // Preserve catalog menu order. If a Row field is present use it; otherwise
        // fall back to insertion order (which already follows the catalog menu).
        const filteredCatalogs = catalogsArr
          .filter((c) => (dropZeroCatalogs ? (c.subItems?.length ?? 0) > 0 : true))
          .sort((a: any, b: any) => {
            const ra = Number(a?.row); const rb = Number(b?.row);
            const aHas = Number.isFinite(ra); const bHas = Number.isFinite(rb);
            if (aHas && bHas) return ra - rb;
            if (aHas) return -1;
            if (bHas) return 1;
            return 0;
          });

        const typeTotal = filteredCatalogs.reduce(
          (sum, c) => sum + toNum(c.totalamount),
          0
        );

        res[typeKey] = {
          totalAmount: typeTotal,
          items: filteredCatalogs,
        };

        return res;
      },
      {}
    );

    return out;
  };

  return { summarizeAmountByTypeFromSubItems };
}
